/**
 * Target Standards Tab - Manage target standard codes and IDs
 * Handles migration from deprecated codes to new standard IDs
 */

import { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Tabs,
  Tab,
  Alert,
  TextField,
  Button,
  Chip,
  CircularProgress,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Save as SaveIcon,
  Warning as WarningIcon,
} from '@mui/icons-material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { LessonV1_SpineTagsPrediction } from '../../types';
import { predictionsApi } from '../../services/predictions.service';
import { useAppStore } from '../../store/appStore';
import { useNotificationStore } from '../../store/notificationStore';
import SemanticStandardsSearch from './SemanticStandardsSearch';

interface TargetStandardsTabProps {
  prediction: LessonV1_SpineTagsPrediction;
  onDirtyStateChange?: (isDirty: boolean) => void;
  triggerSave?: boolean;
}

export default function TargetStandardsTab({ prediction, onDirtyStateChange, triggerSave }: TargetStandardsTabProps) {
  const { selectedProduct, selectedExperiment } = useAppStore();
  const { showNotification } = useNotificationStore();
  const queryClient = useQueryClient();

  // Inner tab state (0 = Standards/IDs, 1 = Codes)
  const [innerTab, setInnerTab] = useState(0);

  // Local state for editing (codes tab only)
  const [localCodes, setLocalCodes] = useState<string[]>(prediction.target_cc_standard_codes || []);
  const [newCode, setNewCode] = useState('');

  // Dirty state (codes tab)
  const [isCodesDirty, setIsCodesDirty] = useState(false);
  // Dirty state (standards tab)
  const [isStandardsDirty, setIsStandardsDirty] = useState(false);

  // Report combined dirty state to parent
  useEffect(() => {
    onDirtyStateChange?.(isCodesDirty || isStandardsDirty);
  }, [isCodesDirty, isStandardsDirty, onDirtyStateChange]);

  // Check if IDs exist in saved prediction (disables codes tab)
  const hasStandardIds = prediction.target_standard_ids && prediction.target_standard_ids.length > 0;

  // Sync local state when prediction changes (codes tab only)
  useEffect(() => {
    setLocalCodes(prediction.target_cc_standard_codes || []);
    setIsCodesDirty(false);
  }, [prediction.target_cc_standard_codes]);

  // Handle external save trigger for codes tab
  useEffect(() => {
    if (triggerSave && isCodesDirty) {
      updateMutation.mutate({ target_cc_standard_codes: localCodes });
    }
  }, [triggerSave]);

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: (updates: Partial<LessonV1_SpineTagsPrediction>) =>
      predictionsApi.update(selectedProduct, selectedExperiment, prediction.id, updates),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['prediction', selectedProduct, selectedExperiment, prediction.id] });
      queryClient.invalidateQueries({ queryKey: ['predictions'] });
      showNotification('success', 'Target standards saved successfully');
      setIsCodesDirty(false);
    },
    onError: (error: any) => {
      showNotification('error', `Failed to save: ${error.message}`);
    },
  });

  // Handlers for codes
  const handleAddCode = () => {
    const trimmed = newCode.trim();
    if (trimmed && !localCodes.includes(trimmed)) {
      setLocalCodes([...localCodes, trimmed]);
      setNewCode('');
      setIsCodesDirty(true);
    }
  };

  const handleDeleteCode = (index: number) => {
    setLocalCodes(localCodes.filter((_, i) => i !== index));
    setIsCodesDirty(true);
  };

  // Revert handler for codes
  const handleRevertCodes = () => {
    setLocalCodes(prediction.target_cc_standard_codes || []);
    setNewCode('');
    setIsCodesDirty(false);
  };

  // Save handler for codes tab
  const handleSave = () => {
    updateMutation.mutate({ target_cc_standard_codes: localCodes });
  };

  // Tab change handler
  const handleTabChange = (_: React.SyntheticEvent, newValue: number) => {
    if (newValue === 1 && hasStandardIds) {
      // Don't allow switching to codes tab if IDs exist
      return;
    }
    setInnerTab(newValue);
  };

  return (
    <Box sx={{ p: 2 }}>
      {/* Inner Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
        <Tabs value={innerTab} onChange={handleTabChange}>
          <Tab label="Target Standards (New)" />
          <Tab
            label={hasStandardIds ? "Target Codes (Disabled - IDs in use)" : "Target Codes (Deprecated)"}
            disabled={!!hasStandardIds}
            sx={hasStandardIds ? { opacity: 0.5 } : {}}
          />
        </Tabs>
      </Box>

      {/* Tab Content */}
      <Box sx={{ minHeight: 300 }}>
        {/* Standards/IDs Tab - Semantic Search */}
        {innerTab === 0 && (
          <SemanticStandardsSearch
            initialStandardIds={prediction.target_standard_ids || []}
            onSave={async (standardIds) => {
              await updateMutation.mutateAsync({ target_standard_ids: standardIds });
            }}
            isSaving={updateMutation.isPending}
            onDirtyStateChange={setIsStandardsDirty}
            triggerSave={triggerSave}
          />
        )}

        {/* Codes Tab */}
        {innerTab === 1 && (
          <Box>
            {/* Deprecation Banner */}
            <Alert severity="info" sx={{ mb: 3 }} icon={<WarningIcon />}>
              <Typography variant="body2">
                <strong>Target Standard codes are deprecated.</strong> It is better to use Target Standard IDs.
                If you choose to use Standard IDs, target codes will be deleted and we will use target standard IDs
                in their place. To use standard IDs, simply add target standard IDs in the other tab, and we will do the rest!
              </Typography>
            </Alert>

            <Typography variant="h6" gutterBottom>
              Target Standard Codes
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              These are the expected Common Core standard codes for this lesson (e.g., 8.G.A.1, 8.G.A.2).
            </Typography>

            {/* Add new code */}
            <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
              <TextField
                size="small"
                placeholder="Enter standard code (e.g., 8.G.A.1)"
                value={newCode}
                onChange={(e) => setNewCode(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleAddCode()}
                sx={{ flex: 1, maxWidth: 400 }}
              />
              <Button
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={handleAddCode}
                disabled={!newCode.trim()}
              >
                Add
              </Button>
            </Box>

            {/* List of codes */}
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 3 }}>
              {localCodes.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  No target codes defined.
                </Typography>
              ) : (
                localCodes.map((code, index) => (
                  <Chip
                    key={index}
                    label={code}
                    onDelete={() => handleDeleteCode(index)}
                    deleteIcon={<DeleteIcon />}
                    variant="outlined"
                  />
                ))
              )}
            </Box>

            {/* Action buttons */}
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                variant="contained"
                startIcon={updateMutation.isPending ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
                onClick={handleSave}
                disabled={!isCodesDirty || updateMutation.isPending}
              >
                {updateMutation.isPending ? 'Saving...' : 'Save Codes'}
              </Button>
              <Button
                variant="outlined"
                onClick={handleRevertCodes}
                disabled={!isCodesDirty || updateMutation.isPending}
              >
                Revert
              </Button>
            </Box>
          </Box>
        )}
      </Box>

    </Box>
  );
}
